import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './Carousel.css';
import bg from "../../assets/throne.png";

const EventCard = ({ eventData }) => {
  const [, setIsScrolling] = useState(false);
  const containerRef = useRef(null);
  let scrollTimeout;
  
  const handleScroll = () => {
    window.scrollBy({
      top: window.innerHeight, // Scroll by 100vh
      behavior: 'smooth', // Smooth scrolling effect
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleScroll();
          }
        });
      },
      { threshold: 0.5 }
    );

    Array.from(container.children).forEach((child) => {
      observer.observe(child);
    });

    return () => {
      observer.disconnect();
      clearTimeout(scrollTimeout);
    };
  }, []);

  const { name, description, rules, venue, teamSize, time, poster, path } = eventData;

  // Conditionally set the registration link based on the path
  const registrationLink = path === '/workshop'
    ? 'https://docs.google.com/forms/d/e/1FAIpQLSePWiapm6qDBjN7hVTjJCnU4JEQKXFcyvVQR76d8-8VVVHd8A/viewform'  // Link for /workshop
    : 'https://docs.google.com/forms/d/e/1FAIpQLSd6sKWFJsDMbNtaIVyGpEdilxImJXRCOZj3-LLnHIGt28OtXg/viewform'; // Default link for other events

  return (
    <div 
      ref={containerRef}
      className="relative h-screen w-screen overflow-y-scroll snap-y scrollable-background snap-mandatory scroll-smooth bg-cover bg-center"
      style={{
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundAttachment: 'fixed',
        backgroundRepeat:"no-repeat"
      }}
    >
      {/* Title Section */}
      <section className="h-screen w-full snap-start flex items-center justify-center flex-col transition-transform duration-1000 relative">
        <div className="text-center px-4 md:px-8">
          <h1 className="text-4xl leading-10 md:text-3xl lg:text-7xl event-title m-4 p-4 md:p-7 title-event tracking-widest">
            {name}
          </h1>
        </div>
        <div className="absolute bottom-20 flex justify-end" onClick={handleScroll}>
          <div className="relative flex flex-col items-center justify-center gap-1">
            <span className="w-10 h-10 block border-r-4 border-b-4 border-white rotate-45 -m-1 rounded animate-custom"></span>
            <span className="w-10 h-10 block border-r-4 border-b-4 border-white rotate-45 -m-1 rounded animate-custom delay-200"></span>
            <span className="w-10 h-10 block border-r-4 border-b-4 border-white rotate-45 -m-1 rounded animate-custom delay-400"></span>
          </div>
        </div>
      </section>

      <section className="h-screen w-full snap-start flex items-center justify-center transition-transform duration-1000 relative">
        <div className="bg-black bg-opacity-60 p-4 rounded-md text-slate-100 text-left">
          <img src={poster} alt={name} className="h-[75vh] object-cover" />
        </div>
      </section>

      {/* Description Section */}
      <section className="h-screen w-full snap-start flex items-center justify-center transition-transform duration-1000 relative tracking-widest ">
        <div className="w-full max-w-lg md:max-w-4xl bg-opacity-0 rounded-xl p-4 md:p-6 text-white ">
          <div className="bg-black bg-opacity-60 p-4 rounded-md text-slate-100 text-left">
            <h3 className="text-lg md:text-xl font-semibold mb-2">Description</h3>
            <p className="text-l leading-relaxed text-sm font-sans">
              {description}
            </p>
          </div>
        </div>
      </section>

      {/* Rules and Details Section */}
      <section className="h-screen w-full snap-start flex items-center justify-center transition-transform duration-1000 relative tracking-widest">
        <div className="w-full max-w-lg md:max-w-4xl bg-opacity-0 gap-2 rounded-xl p-3 md:p-3 text-white flex items-center flex-col">
          <div className="bg-black bg-opacity-60 p-2 rounded-md text-slate-100 text-left">
            <h3 className="text-lg md:text-xl font-semibold mb-2">Rules and Details</h3>
            <ul className="list-disc list-inside ml-2 font-sans text-sm">
              {rules.map((rule, index) => (
                <li key={index}>{rule}</li>
              ))}
            </ul>
            <br />
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <strong>Venue:</strong>
              <p className="font-mono" style={{ margin: '0 0 0 8px', fontSize: 'x-large' }}>{venue}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <strong>Team-size:</strong>
              <p className="font-mono" style={{ margin: '0 0 0 8px', fontSize: 'x-large' }}>{teamSize}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <strong>Time:</strong>
              <p className="font-mono" style={{ margin: '0 0 0 8px', fontSize: 'x-large' }}>{time}</p>
            </div>
          </div>
          
          {/* Conditional Registration Button */}
          <a
            className="w-[max-content] px-4 py-2 bg-black bg-opacity-65 py-2 px-4 text-gray-200 border-[1px] border-gray-300 rounded cursor-pointer transition-all duration-500 hover:scale-105 hover:text-white"
            href={registrationLink}  // Dynamic link
            target="_blank" 
            rel="noopener noreferrer"
          >
            Register
          </a>
        </div>
      </section>
    </div>
  );
};

EventCard.propTypes = {
  eventData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    rules: PropTypes.arrayOf(PropTypes.string).isRequired,
    venue: PropTypes.string.isRequired,
    teamSize: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired, // Added path validation
  }).isRequired,
};

export default EventCard;
